import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, LogOut, Bell, Languages, Crown } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { Field, TextField } from '@/components/FormControls';
import { LanguageToggle } from '@/components/LanguageToggle';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';

export default function Settings() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({
    name: '', email: '', monthlyIncomeGoal: '', mainLifeFocus: '', planningStyle: '', notifications: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        monthlyIncomeGoal: user.monthlyIncomeGoal ?? '',
        mainLifeFocus: user.mainLifeFocus || '',
        planningStyle: user.planningStyle || 'Focused deep work',
        notifications: user.notifications ?? true,
      });
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/profile', {
        name: form.name,
        email: form.email,
        monthlyIncomeGoal: Number(form.monthlyIncomeGoal) || 0,
        mainLifeFocus: form.mainLifeFocus,
        planningStyle: form.planningStyle,
        notifications: form.notifications,
      });
      await refreshUser();
      toast.success(t('settings.updated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <AppLayout>
      <AppHeader title={t('settings.title')} subtitle={t('settings.subtitle')} back />

      <div className="mt-3 glass-card rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-cyan/15 text-brand-cyan">
            <User className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{form.name || t('settings.yourName')}</p>
            <p className="truncate text-xs text-foreground/55">{form.email}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 glass-card flex items-center justify-between rounded-2xl p-4">
        <div className="flex items-center gap-2">
          <Languages className="h-4 w-4 text-brand-cyan" />
          <Label className="text-sm text-foreground/85">{t('settings.languageRow')}</Label>
        </div>
        <LanguageToggle size="md" />
      </div>

      <div
        data-testid="settings-premium-card"
        className="mt-4 rounded-2xl border border-brand-violet/25 bg-gradient-to-br from-brand-violet/[0.12] to-white/[0.03] p-4"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-brand-violet" />
            <p className="text-sm font-semibold text-foreground">{t('settings.premiumTitle')}</p>
          </div>
          <span className="shrink-0 rounded-full border border-brand-violet/30 bg-brand-violet/10 px-2.5 py-1 text-[11px] font-semibold text-violet-100">
            {t('settings.premiumBadge')}
          </span>
        </div>
        <p className="mt-2 text-xs leading-5 text-foreground/65">{t('settings.premiumText')}</p>
      </div>

      <div className="mt-4 space-y-4">
        <TextField label={t('settings.name')} testId="settings-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('settings.yourName')} />
        <TextField label={t('settings.email')} testId="settings-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
        <TextField label={t('settings.incomeGoal')} testId="settings-income-goal-input" type="number" value={form.monthlyIncomeGoal} onChange={(e) => setForm({ ...form, monthlyIncomeGoal: e.target.value })} placeholder="10000" />
        <Field label={t('settings.focus')}>
          <Textarea
            data-testid="settings-focus-input"
            value={form.mainLifeFocus}
            onChange={(e) => setForm({ ...form, mainLifeFocus: e.target.value })}
            placeholder={t('settings.focusPlaceholder')}
            className="min-h-[64px] rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40"
          />
        </Field>
        <TextField label={t('settings.planning')} testId="settings-planning-input" value={form.planningStyle} onChange={(e) => setForm({ ...form, planningStyle: e.target.value })} placeholder={t('settings.planningPlaceholder')} />

        <div className="glass-card flex items-center justify-between rounded-2xl p-4">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-brand-cyan" />
            <Label className="text-sm text-foreground/85">{t('settings.notifications')}</Label>
          </div>
          <Switch
            data-testid="settings-notifications-switch"
            checked={form.notifications}
            onCheckedChange={(v) => setForm({ ...form, notifications: v })}
          />
        </div>
      </div>

      <Button
        data-testid="settings-save-button"
        onClick={save}
        disabled={saving}
        className="btn-brand mt-5 h-12 w-full rounded-xl text-base font-semibold text-brand-ink"
      >
        {t('settings.save')}
      </Button>

      <button
        data-testid="settings-logout-button"
        onClick={doLogout}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-rose-300/20 bg-rose-400/10 py-3 text-sm font-medium text-rose-200 hover:bg-rose-400/15"
      >
        <LogOut className="h-4 w-4" /> {t('settings.logout')}
      </button>
    </AppLayout>
  );
}
